/**
 * Product Integrations Hook
 * 
 * Manages product integrations for companies.
 * 
 * DEMO MODE: Uses localStorage for persistence
 * LIVE MODE: Will be wired to backend API
 * 
 * Backend API Requirements (for future implementation):
 * - GET /api/companies/{companyId}/integrations - Fetch all integrations for a company
 * - POST /api/companies/{companyId}/integrations - Add a new integration
 * - PUT /api/companies/{companyId}/integrations/{integrationId} - Update an integration
 * - DELETE /api/companies/{companyId}/integrations/{integrationId} - Delete an integration
 * - POST /api/companies/{companyId}/integrations/{integrationId}/sync - Trigger sync
 */

import { useState, useEffect } from 'react';
import type { ProductIntegration } from '@/types/product.types';

const STORAGE_KEY = 'product-integrations';

/**
 * Get all product integrations from localStorage
 * Exported for use in other components that need to check all integrations
 */
export const getIntegrationsFromStorage = (): ProductIntegration[] => {
  if (typeof window === 'undefined') return [];
  
  const stored = localStorage.getItem(STORAGE_KEY);
  if (!stored) return [];
  
  try {
    const parsed = JSON.parse(stored);
    // Convert date strings back to Date objects
    return parsed.map((integration: any) => ({
      ...integration,
      lastSync: new Date(integration.lastSync),
      lastAttemptedAt: integration.lastAttemptedAt ? new Date(integration.lastAttemptedAt) : undefined,
      lastSyncedAt: integration.lastSyncedAt ? new Date(integration.lastSyncedAt) : undefined,
      disconnectedAt: integration.disconnectedAt ? new Date(integration.disconnectedAt) : undefined,
    }));
  } catch (error) {
    console.error('Failed to parse integrations from localStorage:', error);
    return [];
  }
};

/**
 * Save product integrations to localStorage
 */
const saveIntegrationsToStorage = (integrations: ProductIntegration[]) => {
  if (typeof window === 'undefined') return;
  
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(integrations));
    // Dispatch custom event for same-tab updates
    window.dispatchEvent(new Event('product-integrations-updated'));
  } catch (error) {
    console.error('Failed to save integrations to localStorage:', error);
  }
};

export const useProductIntegrations = (companyId: string) => {
  const [integrations, setIntegrations] = useState<ProductIntegration[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Load integrations on mount, when companyId changes, or when storage updates
  useEffect(() => {
    const loadIntegrations = () => {
      const allIntegrations = getIntegrationsFromStorage();
      const companyIntegrations = allIntegrations.filter(
        (integration) => integration.companyId === companyId
      );
      setIntegrations(companyIntegrations);
      setIsLoading(false);
    };

    // Initial load
    loadIntegrations();

    // Listen for storage updates (from other tabs or same tab)
    const handleStorageUpdate = () => {
      loadIntegrations();
    };

    window.addEventListener('product-integrations-updated', handleStorageUpdate);
    window.addEventListener('storage', handleStorageUpdate);

    return () => {
      window.removeEventListener('product-integrations-updated', handleStorageUpdate);
      window.removeEventListener('storage', handleStorageUpdate);
    };
  }, [companyId]);

  /**
   * Add a new product integration
   * 
   * LIVE MODE: This will call POST /api/companies/{companyId}/integrations
   * with the integration data and return the created integration from the backend.
   */
  const addIntegration = (integration: Omit<ProductIntegration, 'id'>) => {
    const newIntegration: ProductIntegration = {
      ...integration,
      id: `integration-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    };

    // Update local state
    const updatedIntegrations = [...integrations, newIntegration];
    setIntegrations(updatedIntegrations);

    // Update storage
    const allIntegrations = getIntegrationsFromStorage();
    const otherIntegrations = allIntegrations.filter(
      (int) => int.companyId !== companyId
    );
    saveIntegrationsToStorage([...otherIntegrations, ...updatedIntegrations]);

    return newIntegration;
  };

  /**
   * Add multiple product integrations at once
   * 
   * LIVE MODE: This will call POST /api/companies/{companyId}/integrations/batch
   * with an array of integration data and return the created integrations from the backend.
   */
  const addIntegrations = (integrationsToAdd: Omit<ProductIntegration, 'id'>[]) => {
    const newIntegrations: ProductIntegration[] = integrationsToAdd.map((integration, index) => ({
      ...integration,
      id: `integration-${Date.now()}-${index}-${Math.random().toString(36).substr(2, 9)}`,
    }));

    // Update local state
    const updatedIntegrations = [...integrations, ...newIntegrations];
    setIntegrations(updatedIntegrations);

    // Update storage
    const allIntegrations = getIntegrationsFromStorage();
    const otherIntegrations = allIntegrations.filter(
      (int) => int.companyId !== companyId
    );
    saveIntegrationsToStorage([...otherIntegrations, ...updatedIntegrations]);

    return newIntegrations;
  };

  /**
   * Update an existing product integration
   * 
   * LIVE MODE: This will call PUT /api/companies/{companyId}/integrations/{integrationId}
   */
  const updateIntegration = (
    integrationId: string,
    updates: Partial<ProductIntegration>
  ) => {
    const updatedIntegrations = integrations.map((integration) =>
      integration.id === integrationId
        ? { ...integration, ...updates }
        : integration
    );
    setIntegrations(updatedIntegrations);

    // Update storage
    const allIntegrations = getIntegrationsFromStorage();
    const otherIntegrations = allIntegrations.filter(
      (int) => int.companyId !== companyId
    );
    saveIntegrationsToStorage([...otherIntegrations, ...updatedIntegrations]);
  };

  /**
   * Delete a product integration
   * 
   * LIVE MODE: This will call DELETE /api/companies/{companyId}/integrations/{integrationId}
   */
  const deleteIntegration = (integrationId: string) => {
    const updatedIntegrations = integrations.filter(
      (integration) => integration.id !== integrationId
    );
    setIntegrations(updatedIntegrations);

    // Update storage
    const allIntegrations = getIntegrationsFromStorage();
    const otherIntegrations = allIntegrations.filter(
      (int) => int.companyId !== companyId
    );
    saveIntegrationsToStorage([...otherIntegrations, ...updatedIntegrations]);
  };

  /**
   * Trigger a sync for a product integration
   * 
   * LIVE MODE: This will call POST /api/companies/{companyId}/integrations/{integrationId}/sync
   * and update the lastSync timestamp from the backend response.
   */
  const syncIntegration = (integrationId: string) => {
    updateIntegration(integrationId, {
      lastSync: new Date(),
      status: 'connected',
    });
  };

  return {
    integrations,
    isLoading,
    addIntegration,
    addIntegrations,
    updateIntegration,
    deleteIntegration,
    syncIntegration,
  };
};
